import React, { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import config from '../config';
import { useData } from "../data/DataContext";
import { INDUSTRIES, formatDate } from "../data/blogHelpers";
import "./CaseStudiesPage.css";

export default function CaseStudiesPage() {
  const { caseStudies, loading } = useData();
  const [search, setSearch] = useState("");
  const [activeIndustry, setActiveIndustry] = useState("All");

  const navigate = useNavigate();
  const location = useLocation();

  const handleContactClick = (e) => {
    e.preventDefault();

    const scrollToContact = () => {
      const el = document.getElementById("contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToContact, 300);
      return;
    }

    scrollToContact();
  };

  const filtered = useMemo(() => {
    return caseStudies.filter((cs) => {
      const matchIndustry =
        activeIndustry === "All" || cs.industry === activeIndustry;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        cs.title.toLowerCase().includes(q) ||
        cs.excerpt.toLowerCase().includes(q) ||
        cs.tags?.some((t) => t.toLowerCase().includes(q));
      return matchIndustry && matchSearch;
    });
  }, [caseStudies, search, activeIndustry]);

  const featured = filtered.filter((c) => c.featured).slice(0, 1)[0];
  const rest = filtered.filter((c) => !featured || c.id !== featured.id);

  if (loading) {
    return (
      <div className="case-studies-page">
        <section className="cs-hero">
          <div className="cs-hero__inner">
            <h1 className="cs-hero__title">Client Success Stories</h1>
          </div>
        </section>
        <div className="cs-body">
          <p className="cs-count">Loading case studies…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="case-studies-page">
      {/* ── Hero Banner ── */}
      <section className="cs-hero">
        <div className="cs-hero__inner">

          <h1 className="cs-hero__title">Client Success Stories</h1>
          <p className="cs-hero__subtitle">
            Real engagements, real numbers — see how our fractional CFOs <br/>helped
            UAE businesses solve specific financial challenges.
          </p>

          {/* Search */}
          <div className="cs-search">
            <span className="cs-search__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search case studies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cs-search__input"
            />
            {search && (
              <button className="cs-search__clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>
        </div>
      </section>

      <div className="cs-body">
        {/* ── Industry Filter ── */}
        <div className="cs-filters">
          {INDUSTRIES.map((ind) => (
            <button
              key={ind}
              className={`cs-filter-btn ${activeIndustry === ind ? "active" : ""}`}
              onClick={() => setActiveIndustry(ind)}
            >
              {ind}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="cs-empty">
            <p>No case studies found for "<strong>{search}</strong>".</p>
            <button onClick={() => { setSearch(""); setActiveIndustry("All"); }}>
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {/* ── Featured Case Study ── */}
            {featured && activeIndustry === "All" && !search && (
              <Link to={`/case-studies/${featured.id}`} className="cs-featured">
                <div className="cs-featured__img-wrap">
                  <img src={featured.image} alt={featured.title} className="cs-featured__img" />
                  <span className="cs-featured__badge">Featured</span>
                </div>
                <div className="cs-featured__content">
                  <div className="cs-featured__meta">
                    {featured.industry && <span className="cs-cat">{featured.industry}</span>}
                    <span className="cs-sep">·</span>
                    <span>{formatDate(featured.date)}</span>
                    <span className="cs-sep">·</span>
                    <span>{featured.readTime}</span>
                  </div>
                  <h2 className="cs-featured__title">{featured.title}</h2>
                  <p className="cs-featured__excerpt">{featured.excerpt}</p>

                  {featured.results?.length > 0 && (
                    <div className="cs-featured__results">
                      {featured.results.slice(0, 3).map((r) => (
                        <div key={r.label} className="cs-featured__result">
                          <strong>{r.value}</strong>
                          <span>{r.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="cs-featured__tags">
                    {featured.tags?.slice(0, 3).map((t) => (
                      <span key={t} className="cs-tag">{t}</span>
                    ))}
                  </div>
                  <span className="cs-featured__cta">
                    Read Case Study
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </span>
                </div>
              </Link>
            )}

            {/* ── Results count ── */}
            <p className="cs-count">
              {(activeIndustry === "All" && !search ? rest : filtered).length} case stud{(activeIndustry === "All" && !search ? rest : filtered).length !== 1 ? "ies" : "y"}
              {activeIndustry !== "All" && ` in ${activeIndustry}`}
              {search && ` matching "${search}"`}
            </p>

            {/* ── Grid ── */}
            <div className="cs-grid">
              {(activeIndustry === "All" && !search ? rest : filtered).map((cs) => (
                <Link key={cs.id} to={`/case-studies/${cs.id}`} className="cs-card">
                  <div className="cs-card__img-wrap">
                    <img src={cs.image} alt={cs.title} className="cs-card__img" />
                    {cs.industry && <span className="cs-card__cat">{cs.industry}</span>}
                  </div>
                  <div className="cs-card__body">
                    <div className="cs-card__meta">
                      <span>{formatDate(cs.date)}</span>
                      <span className="cs-sep">·</span>
                      <span>{cs.readTime}</span>
                    </div>
                    <h3 className="cs-card__title">{cs.title}</h3>
                    <p className="cs-card__excerpt">{cs.excerpt}</p>

                    {cs.results?.length > 0 && (
                      <div className="cs-card__results">
                        {cs.results.slice(0, 2).map((r) => (
                          <div key={r.label} className="cs-card__result">
                            <strong>{r.value}</strong>
                            <span>{r.label}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="cs-card__footer">
                      <div className="cs-card__tags">
                        {cs.tags?.slice(0, 2).map((t) => (
                          <span key={t} className="cs-tag">{t}</span>
                        ))}
                      </div>
                      <span className="cs-card__arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ══ CTA BAND ══ */}
      <section className="wcu-cta">
        <div
          className="wcu-cta-bg"
          style={{ backgroundImage: `url(${config.imageBaseURL}${config.paths.banners}/uae-cfobridge2.webp)` }}
        />
        <div className="wcu-cta-overlay" />
        <div className="wcu-cta-inner">
          <h2>Ready to write your own success story?</h2>
          <p>
            Let CFO Bridge UAE become the strategic financial partner your business deserves — on-demand expertise, real ROI, zero full-time overhead.
          </p>
          <a href="#contact" className="wcu-btn-cta" onClick={handleContactClick}>
            Schedule Consultation
          </a>
        </div>
      </section>

    </div>
  );
}